// Vanilla Canvas2D renderer for the "WhatsApp Messages" template.
// A wide phone-screen mockup on a white background. Each new message spawns a
// chat-bubble icon at a random x position along the bottom, which floats
// upward, avoids overlapping other icons, and settles once it reaches the
// top edge or lands on top of another icon. Hover/click reveals the content.

function seededRandom(seed) {
  let t = seed + 0x6D2B79F5;
  return function () {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashId(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

// Generic messaging-bubble palette (intentionally not a reproduction of any
// brand's logo - just a green chat-bubble motif with light variation).
const ICON_THEMES = [
  { base: '#25D366', shade: '#128C7E', glyph: '#ffffff' },
  { base: '#34c98f', shade: '#0e9e73', glyph: '#ffffff' },
  { base: '#2bb673', shade: '#0f8a5b', glyph: '#ffffff' },
];

const GAP = 8; // min gap between icon edges — icons must not overlap

function drawPhoneFrame(ctx, width, height) {
  const margin = Math.min(width, height) * 0.035;
  const r = 28;

  // Bezel
  ctx.fillStyle = '#1c1c1e';
  ctx.beginPath();
  ctx.roundRect(margin * 0.4, margin * 0.4, width - margin * 0.8, height - margin * 0.8, r + 10);
  ctx.fill();

  // Screen
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(margin, margin, width - margin * 2, height - margin * 2, r);
  ctx.fill();

  // Notch (top center, since this is a wide/landscape phone mockup the notch sits on the short top edge)
  const notchW = Math.min(140, width * 0.12);
  const notchH = margin * 0.55;
  ctx.fillStyle = '#1c1c1e';
  ctx.beginPath();
  ctx.roundRect(width / 2 - notchW / 2, margin, notchW, notchH, notchH / 2);
  ctx.fill();

  // Home indicator (bottom center)
  ctx.fillStyle = '#d0d0d5';
  ctx.beginPath();
  ctx.roundRect(width / 2 - 70, height - margin - 6, 140, 4, 2);
  ctx.fill();

  return {
    left: margin,
    top: margin + notchH + 14,
    right: width - margin,
    bottom: height - margin - 24,
  };
}

function drawIcon(ctx, x, y, size, theme, isHover, scale) {
  const r = (size / 2) * scale;
  ctx.save();
  ctx.translate(x, y);

  // Shadow
  ctx.beginPath();
  ctx.ellipse(0, r * 0.85, r * 0.75, r * 0.22, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.fill();

  // Bubble body (rounded square with a tail, like a chat bubble)
  const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.15, 0, 0, r * 1.1);
  grad.addColorStop(0, theme.base);
  grad.addColorStop(1, theme.shade);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(-r, -r, r * 2, r * 1.7, r * 0.55);
  ctx.fill();
  // tail
  ctx.beginPath();
  ctx.moveTo(-r * 0.15, r * 0.68);
  ctx.lineTo(-r * 0.5, r * 1.1);
  ctx.lineTo(r * 0.05, r * 0.7);
  ctx.closePath();
  ctx.fill();

  // Glyph: simple speech dots
  ctx.fillStyle = theme.glyph;
  for (const dx of [-0.32, 0, 0.32]) {
    ctx.beginPath();
    ctx.arc(dx * r, -r * 0.08, r * 0.12, 0, Math.PI * 2);
    ctx.fill();
  }

  if (isHover) {
    ctx.beginPath();
    ctx.roundRect(-r * 1.15, -r * 1.15, r * 2.3, r * 2.3, r * 0.7);
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  ctx.restore();
}

export function createWhatsAppStage(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const messages = new Map();
  const floaters = [];
  let bounds = { left: 0, top: 0, right: 0, bottom: 0 };
  let hoverId = null;
  let selectCb = opts.onSelect || null;
  let hoverCb = opts.onHover || null;
  let running = true;
  let rafId = null;
  let lastNow = performance.now();

  let bgImage = null;
  let bgImageReady = false;
  const bgUrl = opts.backgroundImageUrl || opts.backgroundImageUrl;
  if (bgUrl) {
    bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.onload = () => { bgImageReady = true; };
    bgImage.src = bgUrl;
  }

  function contentBounds() {
    if (bgImageReady) {
      return { left: width * 0.05, top: height * 0.07, right: width * 0.95, bottom: height * 0.93 };
    }
    const margin = Math.min(width, height) * 0.035;
    const notchH = margin * 0.55;
    return {
      left: margin,
      top: margin + notchH + 14,
      right: width - margin,
      bottom: height - margin - 24,
    };
  }

  function iconRadius(m) {
    return m.size * 0.55;
  }

  function drawCoverImage(img) {
    const scale = Math.max(width / img.width, height / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
  }

  function resize() {
    const rect = canvas.parentElement.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    bounds = contentBounds();
  }

  function pickSpawnX(size, rnd) {
    bounds = contentBounds();
    const r = size * 0.55;
    const minX = bounds.left + r + GAP;
    const maxX = bounds.right - r - GAP;
    let bestX = minX + rnd() * Math.max(1, maxX - minX);
    let bestScore = -Infinity;
    for (let i = 0; i < 28; i++) {
      const x = minX + rnd() * Math.max(1, maxX - minX);
      let score = Infinity;
      for (const other of floaters) {
        const need = r + iconRadius(other) + GAP;
        score = Math.min(score, Math.abs(other.x - x) - need);
      }
      if (score > bestScore) {
        bestScore = score;
        bestX = x;
      }
      if (score >= 0) break;
    }
    return bestX;
  }

  function ensureMessage(msg) {
    if (messages.has(msg.id)) return messages.get(msg.id);
    bounds = contentBounds();
    const seed = hashId(msg.id);
    const rnd = seededRandom(seed);
    const theme = ICON_THEMES[seed % ICON_THEMES.length];
    const size = 46 + rnd() * 10;
    const rec = {
      id: msg.id,
      sender_name: msg.sender_name || msg.sender_name,
      message_body: msg.message_body || msg.message_body,
      theme,
      size,
      x: pickSpawnX(size, rnd),
      y: bounds.bottom + size,
      speed: 28 + rnd() * 18,
      settled: false,
      spawnTime: performance.now(),
    };
    messages.set(msg.id, rec);
    floaters.push(rec);
    return rec;
  }

  function setMessages(newMessages) {
    for (const msg of newMessages) ensureMessage(msg);
  }

  function blockedYBelow(m, other, nextY) {
    const minDist = iconRadius(m) + iconRadius(other) + GAP;
    const dx = other.x - m.x;
    if (Math.abs(dx) >= minDist) return nextY;
    const maxDy = Math.sqrt(Math.max(0, minDist * minDist - dx * dx));
    const sitBelow = other.y + maxDy;
    if (m.y + iconRadius(m) >= other.y - maxDy && nextY < sitBelow) {
      return sitBelow;
    }
    return nextY;
  }

  function updateFloaters(dt) {
    bounds = contentBounds();
    const rising = floaters.filter((m) => !m.settled).sort((a, b) => a.y - b.y);
    for (const m of rising) {
      const r = iconRadius(m);
      const ceiling = bounds.top + r + GAP;
      let nextY = m.y - m.speed * dt;
      nextY = Math.max(nextY, ceiling);
      for (const other of floaters) {
        if (other === m) continue;
        nextY = blockedYBelow(m, other, nextY);
      }
      m.y = nextY;

      const atTop = m.y <= ceiling + 0.45;
      let hitOther = false;
      for (const other of floaters) {
        if (other === m) continue;
        const minDist = r + iconRadius(other) + GAP;
        const dist = Math.hypot(other.x - m.x, other.y - m.y);
        if (dist < minDist + 0.6 && other.y <= m.y) {
          hitOther = true;
          break;
        }
      }
      if (atTop || hitOther) m.settled = true;
    }

    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < floaters.length; i++) {
        for (let j = i + 1; j < floaters.length; j++) {
          const a = floaters[i];
          const b = floaters[j];
          const minDist = iconRadius(a) + iconRadius(b) + GAP;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy) || 0.0001;
          if (dist >= minDist) continue;
          const overlap = minDist - dist;
          const lower = a.y >= b.y ? a : b;
          lower.y += overlap;
          lower.settled = true;
        }
      }
    }
  }

  function drawBackground() {
    if (bgImageReady) {
      drawCoverImage(bgImage);
      bounds = contentBounds();
      return;
    }
    ctx.fillStyle = '#eef1f4';
    ctx.fillRect(0, 0, width, height);
    bounds = drawPhoneFrame(ctx, width, height);
  }

  function frame(now) {
    if (!running) return;
    const dt = Math.min(0.05, (now - lastNow) / 1000);
    lastNow = now;

    drawBackground();

    updateFloaters(dt);

    ctx.save();
    ctx.beginPath();
    ctx.rect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top);
    ctx.clip();

    for (const m of floaters) {
      const age = performance.now() - m.spawnTime;
      const spawnScale = 0.6 + 0.4 * Math.min(1, age / 300);
      drawIcon(ctx, m.x, m.y, m.size, m.theme, m.id === hoverId, spawnScale);
    }
    ctx.restore();

    rafId = requestAnimationFrame(frame);
  }

  function hitTest(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    let found = null;
    let bestDist = Infinity;
    for (const m of floaters) {
      const dx = m.x - px;
      const dy = m.y - py;
      const dist = Math.hypot(dx, dy);
      const hitR = m.size * 0.65;
      if (dist <= hitR && dist < bestDist) {
        bestDist = dist;
        found = m;
      }
    }
    return found ? { obj: found, x: px, y: py } : null;
  }

  function handleMove(e) {
    const hit = hitTest(e.clientX, e.clientY);
    const id = hit ? hit.obj.id : null;
    if (id !== hoverId) {
      hoverId = id;
      canvas.style.cursor = id ? 'pointer' : 'default';
      if (hoverCb) hoverCb(hit ? { id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y } : null);
    } else if (hit && hoverCb) {
      hoverCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y });
    }
  }

  function handleClick(e) {
    const hit = hitTest(e.clientX, e.clientY);
    if (hit && selectCb) {
      selectCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body }, { x: hit.x, y: hit.y });
    }
  }

  function handleLeave() {
    if (hoverId !== null) {
      hoverId = null;
      canvas.style.cursor = 'default';
      if (hoverCb) hoverCb(null);
    }
  }

  canvas.addEventListener('pointermove', handleMove);
  canvas.addEventListener('click', handleClick);
  canvas.addEventListener('pointerleave', handleLeave);

  resize();
  rafId = requestAnimationFrame(frame);

  return {
    setMessages,
    onSelect(cb) { selectCb = cb; },
    onHover(cb) { hoverCb = cb; },
    resize,
    destroy() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      canvas.removeEventListener('pointermove', handleMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('pointerleave', handleLeave);
    },
  };
}
